import React, { FC, useEffect, useState } from "react";
import { Container, Alert } from "../../../../components";
import { Button, Heading, Text, Icon, Link, Box } from "@telia/teddy";
import { connect, useDispatch } from "react-redux";
import { BankIdRefererType } from "../../../../../../constants";
import { AnnouncementType, RootState } from "../../../../../../models";
import BankIdCard from "../../../../authentication/components/organisms/BankIdCard/BankIdCard";
import { BankIdTypes, BANK_ID_TYPES } from "../../../../authentication/consts";
import { bankIdAuthorize2, AuthMethodType } from "../../../../authentication/state/actions";
import {
    getBankIdUrl as getBankIdUrlSelector,
    isBankIdVerified as isBankIdVerifiedSelector,
    isBankIdAvailable as isBankIdAvailableSelector,
    isBankIdMandatory as isBankIdMandatorySelector,
    isBankIdPerformed as isBankIdPerformedSelector,
    shouldShowBankId as shouldShowBankIdSelector,
    hasBankIdError,
    getBankIdErrorMessage,
    getRequestor,
} from "../../../../authentication/state/selectors";
import AUTHENTICATION_TEXT from "../../../../authentication/text-consts";
import { useFeatureToggleWithQueryParam, usePrevious } from "../../../hooks";
import { isHwOnlyButRequireBankid } from "../../../state/selectors/combinedSelectors";
import { hasOnlyPrepaidSubscriptions as hasOnlyPrepaidSubscriptionsSelector } from "../../../state/selectors/subscriptionsSelectors";
import TEXT from "../../../text-consts";
import "./BankIdSection.less";

interface StateProps {
    bankIdErrorMessage?: string;
    bankIdUrl?: string;
    hasError: boolean;
    hasOnlyPrepaidSubscriptions: boolean;
    isBankIdAvailable: boolean;
    isBankIdMandatory: boolean;
    isBankIdPerformed: boolean;
    isBankIdVerified: boolean;
    isHardwareOnly?: boolean;
    requestor?: string;
    shouldShowBankId: boolean;
}

interface Props extends StateProps {
    referer?: BankIdRefererType;
    skipDisplayDescription?: boolean;
    skipDisplayError?: boolean;
    skipDisplayHeading?: boolean;
    typeId?: AnnouncementType;
}

const BankIdSectionTeliaBroker: FC<Props> = ({
    referer,
    typeId,
    skipDisplayHeading = false,
    skipDisplayDescription = false,
    skipDisplayError = false,
    //redux
    bankIdUrl,
    hasError,
    isBankIdVerified,
    isBankIdPerformed,
    isBankIdAvailable,
    isBankIdMandatory,
    isHardwareOnly,
    shouldShowBankId,
    hasOnlyPrepaidSubscriptions,
    bankIdErrorMessage,
    requestor,
}) => {
    const dispatch = useDispatch();
    const [bankIdType, setBankIdType] = useState<BankIdTypes | null>(null);
    const previousBankIdURL = usePrevious(bankIdUrl);
    const normalizedRequestor = requestor?.toLowerCase();
    const isCandourEnabled =
        useFeatureToggleWithQueryParam("enableCandour", "candour") &&
        (!normalizedRequestor ||
            normalizedRequestor === "sfb" ||
            normalizedRequestor === "sfc" ||
            normalizedRequestor === "mm") &&
        referer === "auth-user";

    const getDescription = () => {
        switch (true) {
            case referer === "fwa":
                return AUTHENTICATION_TEXT.BANK_ID.DESCRIPTION_FWA_ADDRESS_LOOKUP;
            case referer === "prepaidRegistration":
                return AUTHENTICATION_TEXT.BANK_ID.DESCRIPTION_PREPAID_REGISTRATION;
            case referer === "esim":
                return AUTHENTICATION_TEXT.BANK_ID.DESCRIPTION_ESIM;
            case referer === "auth-user":
                return AUTHENTICATION_TEXT.AUTH_PAGE.INGRESS_NO(normalizedRequestor);
            case referer === "consentB2B":
                return undefined;
            case referer === "onboardingB2B":
                return AUTHENTICATION_TEXT.BANK_ID.DESCRIPTION_ONBOARDING_B2B;
            case hasOnlyPrepaidSubscriptions:
                return AUTHENTICATION_TEXT.BANK_ID.DESCRIPTION_3_ADDRESS_LOOKUP;
            case isHardwareOnly:
                return AUTHENTICATION_TEXT.BANK_ID.DESCRIPTION_2_HW_ONLY_ABOVE_LIMIT;
            default:
                return AUTHENTICATION_TEXT.BANK_ID.DESCRIPTION_2_CREDIT_CHECK;
        }
    };

    useEffect(() => {
        // Reset BankID choice when user has completed a verification
        if (isBankIdVerified || hasError) {
            setBankIdType(null);
        }
    }, [isBankIdVerified, hasError]);

    useEffect(() => {
        if (bankIdUrl && previousBankIdURL !== bankIdUrl) {
            // redirect to BankID URL if it exists
            window.location.href = bankIdUrl;
        }
    }, [bankIdUrl, previousBankIdURL]);

    const handleSubmit = (authMethod: AuthMethodType = "BANK_ID") => {
        dispatch(bankIdAuthorize2(BANK_ID_TYPES.BANK_ID, referer, authMethod));
        setBankIdType(BANK_ID_TYPES.BANK_ID);
    };

    function shouldShowBankIdNotAvailableWhenMandatory() {
        return !isBankIdAvailable && isBankIdMandatory;
    }

    function renderBankIdAlerts() {
        if (bankIdErrorMessage && !skipDisplayError) {
            return (
                <Container className="beta-checkout--center" size="small" noPadding>
                    <Alert kind="warning">
                        <Text variant="additional-100" dangerouslySetInnerHTML={{ __html: bankIdErrorMessage }}></Text>
                    </Alert>
                </Container>
            );
        }

        if (shouldShowBankIdNotAvailableWhenMandatory()) {
            return (
                <Container className="beta-checkout--center" size="small" noMargin noPadding>
                    <Alert kind="warning">
                        {TEXT.PERSONAL_DETAILS.VERIFICATION.BANK_ID_MANDATORY_BUT_NOT_AVAILABLE}
                    </Alert>
                </Container>
            );
        }

        return null;
    }

    const bankIdAlert = renderBankIdAlerts();
    const description = getDescription();

    return (
        !isBankIdPerformed &&
        !isBankIdVerified && (
            <Container className="bankid-section__bankid-container" size="medium">
                <div className="bankid-section__bankid-wrapper">
                    {!skipDisplayHeading && (
                        <Heading as="h2" variant="title-200" mb="150">
                            {AUTHENTICATION_TEXT.BANK_ID.HEADING}
                        </Heading>
                    )}
                    <Box mb="600">
                        {description && !skipDisplayDescription && (
                            <Text
                                className="bankid-section__bankid-container__description"
                                as="p"
                                variant="paragraph-100"
                            >
                                {description}
                            </Text>
                        )}
                    </Box>
                    {bankIdAlert}
                    {!bankIdType && (
                        <>
                            <Button
                                onClick={() => handleSubmit("BANK_ID")}
                                className="bankid-section__bankid-button"
                                variant="primary"
                            >
                                <span>
                                    <Icon name="bankid-norway" aria-hidden="true" focusable="false" />
                                </span>
                                Verifisering via BankID
                            </Button>

                            {isCandourEnabled && (
                                <div className="bankid-section__secondary-option">
                                    <Text>{AUTHENTICATION_TEXT.CANDOUR.HEADING} </Text>
                                    <Link
                                        aria-label="candour-id"
                                        variant="text"
                                        onClick={() => handleSubmit("CANDOUR")}
                                    >
                                        {AUTHENTICATION_TEXT.CANDOUR.SUBHEADING}
                                    </Link>
                                </div>
                            )}
                        </>
                    )}
                    {shouldShowBankId && bankIdType && <BankIdCard typeId={typeId} />}
                </div>
            </Container>
        )
    );
};

const mapStateToProps: (state: RootState) => StateProps = (state) => ({
    bankIdUrl: getBankIdUrlSelector(state),
    isBankIdVerified: isBankIdVerifiedSelector(state),
    isBankIdPerformed: isBankIdPerformedSelector(state),
    isBankIdAvailable: isBankIdAvailableSelector(state),
    isBankIdMandatory: isBankIdMandatorySelector(state),
    shouldShowBankId: shouldShowBankIdSelector(state),
    hasError: hasBankIdError(state),
    hasOnlyPrepaidSubscriptions: hasOnlyPrepaidSubscriptionsSelector(state),
    bankIdErrorMessage: getBankIdErrorMessage(state),
    requestor: getRequestor(state),
    isHardwareOnly: isHwOnlyButRequireBankid(state),
});

export default connect(mapStateToProps)(BankIdSectionTeliaBroker);
